/**
 * The AI Clinical Copilot panel (frontend §"AI Clinical Copilot panel").
 *
 * A collapsible column beside the call stage carrying the nine sections the
 * spec lists, in that order. It sits *around* the ZegoCloud stage and never
 * inside it — the call UI is not redesigned, only given a neighbour.
 *
 * The panel's governing behaviour is what it does when things go wrong. An
 * unreachable service, a refused model, a browser with no speech recognition:
 * each of those changes what this column says and nothing else on the screen.
 * The video, the controls and the End call button are untouched by every one of
 * them.
 */

import {
  AlertTriangle,
  ClipboardList,
  FileText,
  FlaskConical,
  HeartPulse,
  Loader2,
  NotebookText,
  PanelRightClose,
  RefreshCw,
  ScrollText,
  Sparkles,
  Stethoscope,
  TestTube,
} from "lucide-react";
import { useEffect } from "react";

import { Button } from "@/components/ui/button";
import type { CopilotController } from "@/hooks/doctor/use-copilot";
import type { TranscriptionController } from "@/hooks/doctor/use-transcription";
import { cn } from "@/lib/utils";
import type { ClinicalBucket } from "@/types/copilot";

import { ClinicalList } from "./clinical-list";
import { CopilotSection, SectionEmpty } from "./copilot-primitives";
import { HistorySection, LabsSection, VitalsSection } from "./context-sections";
import { TranscriptSection } from "./transcript-section";

const FINDINGS: Array<{ bucket: ClinicalBucket; label: string }> = [
  { bucket: "symptoms", label: "Symptoms" },
  { bucket: "complaints", label: "Complaints" },
  { bucket: "conditions", label: "Existing conditions" },
  { bucket: "allergies", label: "Allergies" },
  { bucket: "medications", label: "Current medications" },
];

export function CopilotPanel({
  copilot,
  transcription,
  onCollapse,
  className,
}: {
  copilot: CopilotController;
  transcription: TranscriptionController;
  onCollapse: () => void;
  className?: string | undefined;
}) {
  const { session } = copilot;
  const clinical = session?.clinical;

  // Context is fetched once the session exists; it is not part of opening it,
  // so a slow vitals lookup never delays the panel appearing.
  useEffect(() => {
    if (session?.id && !session.context?.fetchedAt) copilot.loadContext();
  }, [copilot, session?.context?.fetchedAt, session?.id]);

  const findingsCount = FINDINGS.reduce(
    (total, entry) => total + (clinical?.[entry.bucket]?.length ?? 0),
    0,
  );

  return (
    <aside
      className={cn("card-surface flex min-h-0 flex-col overflow-hidden", className)}
      aria-label="AI Clinical Copilot"
    >
      <header className="flex items-center gap-2 border-b border-border px-4 py-3">
        <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
          <Sparkles className="size-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-sm font-semibold">AI Clinical Copilot</h2>
          <p className="truncate text-[0.65rem] text-muted-foreground">
            {copilot.analysing
              ? "Reading the consultation…"
              : "Suggestions for your review — never a decision"}
          </p>
        </div>

        {/* Analysis runs on its own while the call is live; this is a
            read-it-now shortcut, not the way the panel is meant to be used. */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs"
          onClick={copilot.analyse}
          disabled={copilot.analysing || !session || copilot.unavailable}
          title="Read the transcript now. This also happens automatically while the consultation is running."
        >
          {copilot.analysing ? (
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="size-3.5" aria-hidden />
          )}
          <span className="sr-only">Read the transcript now</span>
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={onCollapse}
          aria-label="Hide the AI copilot"
        >
          <PanelRightClose className="size-4" aria-hidden />
        </Button>
      </header>

      <StatusStrip copilot={copilot} />

      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* 1 — Live transcription */}
        <CopilotSection title="Live transcription" icon={ScrollText} defaultOpen>
          <TranscriptSection
            segments={copilot.segments}
            interim={transcription.interim}
            state={transcription.supported ? transcription.state : "unavailable"}
            supported={transcription.supported}
            enabled={transcription.enabled}
            onToggle={transcription.toggle}
          />
        </CopilotSection>

        {/* 2 — Extracted symptoms and history */}
        <CopilotSection
          title="Extracted findings"
          icon={ClipboardList}
          count={findingsCount}
          defaultOpen
        >
          <div className="space-y-3">
            {FINDINGS.map((entry) => (
              <div key={entry.bucket}>
                <p className="mb-1 text-[0.65rem] font-semibold tracking-wide text-muted-foreground uppercase">
                  {entry.label}
                </p>
                <ClinicalList
                  bucket={entry.bucket}
                  items={clinical?.[entry.bucket] ?? []}
                  onAct={copilot.actOnItem}
                  onAdd={copilot.addItem}
                  readOnly={!session}
                />
              </div>
            ))}
          </div>
        </CopilotSection>

        {/* 3 — Possible diagnoses */}
        <CopilotSection
          title="Possible diagnoses"
          icon={Stethoscope}
          count={clinical?.diagnoses?.length ?? 0}
        >
          <p className="mb-2 rounded-md bg-info/8 px-2 py-1.5 text-[0.7rem] leading-relaxed text-foreground">
            These are AI-generated possibilities to consider. The final diagnosis is yours alone and
            is never set automatically.
          </p>
          <ClinicalList
            bucket="diagnoses"
            items={clinical?.diagnoses ?? []}
            onAct={copilot.actOnItem}
            onAdd={copilot.addItem}
            readOnly={!session}
          />
        </CopilotSection>

        {/* 4 — Suggested tests */}
        <CopilotSection
          title="Suggested tests"
          icon={TestTube}
          count={clinical?.tests?.length ?? 0}
        >
          <p className="mb-2 rounded-md bg-info/8 px-2 py-1.5 text-[0.7rem] leading-relaxed text-foreground">
            Nothing is ordered automatically. A test reaches the prescription only when you add it.
          </p>
          <ClinicalList
            bucket="tests"
            items={clinical?.tests ?? []}
            onAct={copilot.actOnItem}
            onAdd={copilot.addItem}
            readOnly={!session}
            showPrescriptionToggle
          />
        </CopilotSection>

        {/* 5 — Vitals */}
        <CopilotSection
          title="Vitals & devices"
          icon={HeartPulse}
          count={session?.context?.vitals?.length ?? 0}
        >
          <VitalsSection context={session?.context} />
        </CopilotSection>

        {/* 6 — Patient history */}
        <CopilotSection title="Patient history" icon={NotebookText}>
          <HistorySection context={session?.context} />
        </CopilotSection>

        {/* 7 — Lab reports */}
        <CopilotSection
          title="Lab report insights"
          icon={FlaskConical}
          count={session?.context?.labs?.length ?? 0}
        >
          <LabsSection context={session?.context} />
        </CopilotSection>

        {/* 8 & 9 — the post-call flow, summarised here and driven after the call */}
        <CopilotSection title="Prescription & summary" icon={FileText}>
          <PostCallStatus copilot={copilot} />
        </CopilotSection>
      </div>
    </aside>
  );
}

/**
 * The one place the panel reports its own health.
 *
 * Kept to a single strip so a degraded copilot reads as a note on a working
 * tool, not as an error screen replacing one.
 */
function StatusStrip({ copilot }: { copilot: CopilotController }) {
  if (copilot.unavailable) {
    return (
      <Strip tone="warning" icon={AlertTriangle}>
        The AI copilot is offline. Your consultation is unaffected — you can still record everything
        and write the prescription yourself.
      </Strip>
    );
  }
  if (copilot.session && !copilot.session.aiAvailable) {
    return (
      <Strip tone="muted" icon={AlertTriangle}>
        AI assistance is not enabled on this server. The transcript and your own notes still work.
      </Strip>
    );
  }
  if (copilot.notice) {
    return (
      <Strip tone="warning" icon={AlertTriangle}>
        {copilot.notice}
      </Strip>
    );
  }
  if (copilot.connecting) {
    return (
      <Strip tone="muted" icon={Loader2} spin>
        Connecting the copilot…
      </Strip>
    );
  }
  return null;
}

function Strip({
  tone,
  icon: Icon,
  spin,
  children,
}: {
  tone: "warning" | "muted";
  icon: typeof AlertTriangle;
  spin?: boolean | undefined;
  children: React.ReactNode;
}) {
  return (
    <div
      role="status"
      className={cn(
        "flex items-start gap-2 border-b border-border px-4 py-2 text-[0.7rem] leading-relaxed",
        tone === "warning" ? "bg-warning/8 text-foreground" : "bg-muted text-muted-foreground",
      )}
    >
      <Icon className={cn("mt-0.5 size-3.5 shrink-0", spin && "animate-spin")} aria-hidden />
      <p>{children}</p>
    </div>
  );
}

/** Sections 8 and 9 during the call: state only — the flow runs after it ends. */
function PostCallStatus({ copilot }: { copilot: CopilotController }) {
  const status = copilot.session?.status;

  if (status === "processing") {
    return (
      <p className="inline-flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" aria-hidden />
        Preparing the prescription draft…
      </p>
    );
  }
  if (copilot.prescription) {
    return (
      <SectionEmpty>
        A prescription draft is ready. It opens for review when you end the consultation.
      </SectionEmpty>
    );
  }
  return (
    <SectionEmpty>
      When you end the consultation, a prescription draft is prepared from what you confirmed here.
      You review, edit, sign and issue it — nothing is sent to the patient before you do.
    </SectionEmpty>
  );
}
